'use client';

import classNames from 'classnames';
import { PropsWithChildren, ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import logTrailDetailsShowMoreClicked from '@alltrails/amplitude/events/logTrailDetailsShowMoreClicked';
import useHasOverflow from '@alltrails/core/hooks/useHasOverflow';
import useToggle from '@alltrails/core/hooks/useToggle';
import IconButton from '@alltrails/denali/components/IconButton';
import EditIcon from '@alltrails/denali/icons/Edit';
import LocalizationButton from '@alltrails/layout/components/LocalizationButton';
import type TrailDetails from '@alltrails/types/TrailDetails';
import useTrailEditToolbar from '@/hooks/useTrailEditToolbar';
import styles from './Description.module.scss';

type Props = {
  hideLocalizationButton?: boolean;
  isBot: boolean;
  isShortened?: boolean;
  source?: ReactNode;
  trailDetails: TrailDetails;
  isAdminOrPLPUser?: boolean;
};

export default function Description({ children, hideLocalizationButton, isBot, isShortened, source, trailDetails, isAdminOrPLPUser }: PropsWithChildren<Props>) {
  const [expanded, toggle] = useToggle();
  const [showSource, toggleShowSource] = useToggle();
  const intl = useIntl();
  const { ref, hasOverflow } = useHasOverflow<HTMLDivElement>();
  const { openTrailEditToolbar } = useTrailEditToolbar();

  return (
    <div className={classNames({ [styles.adminDescription]: isAdminOrPLPUser })}>
      <div
        className={classNames(styles.description, {
          [styles.clamp]: !isBot && !expanded,
          [styles.short]: !isBot && !expanded && !!isShortened,
          [styles.expanded]: expanded
        })}
        ref={ref}
      >
        {showSource ? source : children}
      </div>
      {!isBot && hasOverflow && (
        <>
          {expanded && ' '}
          <button
            className={classNames(styles.button, { [styles.more]: !expanded })}
            onClick={() => {
              toggle();

              logTrailDetailsShowMoreClicked({ trail_id: trailDetails.id, show_more_location: 'overview' });

              if (expanded && ref?.current) {
                ref.current.scrollIntoView();
              }
            }}
            type="button"
          >
            {expanded ? <FormattedMessage defaultMessage="less" /> : <FormattedMessage defaultMessage="more" />}
          </button>
        </>
      )}
      {source && !hideLocalizationButton && (
        <LocalizationButton showSource={showSource} toggleShowSource={toggleShowSource} className={styles.source} />
      )}
      {isAdminOrPLPUser && (
        <IconButton
          icon={{ Component: EditIcon }}
          className={classNames(styles.editIcon, { [styles.expanded]: expanded })}
          variant="primary"
          onClick={() => openTrailEditToolbar('description')}
          size="sm"
          title={intl.formatMessage({ defaultMessage: 'Edit' })}
        />
      )}
    </div>
  );
}
